import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  type ReactNode,
  type UIEvent,
} from 'react';
import styled from 'styled-components';
import { colors, spacing } from '../../theme-v2';

export interface ChatThreadHandle {
  /** Imperatively scroll the thread to the latest message. */
  scrollToBottom: (behavior?: ScrollBehavior) => void;
}

export interface ChatThreadProps {
  /** Message nodes (typically `<ChatMessage>` instances). */
  children: ReactNode;
  /**
   * When `auto`, the thread scrolls to the bottom whenever the message
   * count grows AND the user is already pinned to the bottom. When
   * `never`, scrolling is fully consumer-controlled. Defaults to `auto`.
   */
  autoScroll?: 'auto' | 'never';
  /** Optional empty-state node (shown when no children are passed). */
  emptyState?: ReactNode;
  className?: string;
}

/**
 * ChatThread — scrollable container for chat messages with sticky-bottom
 * behavior. Pins to the bottom on new messages only while the user is
 * already at the bottom (so reading older messages doesn't get yanked).
 *
 * Exposes a ref handle (`scrollToBottom`) for explicit jump-to-bottom
 * buttons. Parent still owns the message list and ordering.
 */
export const ChatThread = forwardRef<ChatThreadHandle, ChatThreadProps>(
  ({ children, autoScroll = 'auto', emptyState, className }, ref) => {
    const scrollRef = useRef<HTMLDivElement>(null);
    const listRef = useRef<HTMLDivElement>(null);
    const pinnedToBottomRef = useRef(true);
    const childCount = countChildren(children);

    const scrollToBottom = useCallback((behavior: ScrollBehavior = 'smooth') => {
      const el = scrollRef.current;
      if (!el) return;
      // An explicit jump declares the intent to follow the latest message —
      // re-pin immediately so growth landing mid-animation (when intermediate
      // scroll positions read as "not at bottom") keeps sticking.
      pinnedToBottomRef.current = true;
      el.scrollTo({ top: el.scrollHeight, behavior });
    }, []);

    useImperativeHandle(ref, () => ({ scrollToBottom }), [scrollToBottom]);

    useEffect(() => {
      if (autoScroll === 'never') return;
      if (!pinnedToBottomRef.current) return;
      scrollToBottom('auto');
    }, [childCount, autoScroll, scrollToBottom]);

    // A streamed answer grows an EXISTING bubble (and its source cards arrive
    // later) without changing the child count, so the count effect above never
    // re-fires. Track the list's size instead and stick to the bottom while
    // the user is pinned there.
    const isEmpty = childCount === 0;
    useEffect(() => {
      if (autoScroll === 'never') return;
      const list = listRef.current;
      if (!list) return;
      const observer = new globalThis.ResizeObserver(() => {
        if (pinnedToBottomRef.current) scrollToBottom('auto');
      });
      observer.observe(list);
      return () => observer.disconnect();
      // `isEmpty` re-arms the observer when the list replaces the empty state.
    }, [autoScroll, scrollToBottom, isEmpty]);

    const handleScroll = (e: UIEvent<HTMLDivElement>) => {
      const el = e.currentTarget;
      const distanceFromBottom =
        el.scrollHeight - el.scrollTop - el.clientHeight;
      // 24px tolerance — counts as "at bottom" for sticky-pin purposes
      pinnedToBottomRef.current = distanceFromBottom < 24;
    };

    return (
      <StyledScroll
        ref={scrollRef}
        className={className}
        onScroll={handleScroll}
        role="log"
        aria-live="polite"
      >
        {isEmpty && emptyState ? (
          <StyledEmptyWrap>{emptyState}</StyledEmptyWrap>
        ) : (
          <StyledList ref={listRef}>{children}</StyledList>
        )}
      </StyledScroll>
    );
  }
);

ChatThread.displayName = 'ChatThread';

const countChildren = (children: ReactNode): number => {
  if (children == null || children === false) return 0;
  if (Array.isArray(children)) return children.filter(Boolean).length;
  return 1;
};

const StyledScroll = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  background: ${colors.surface.base};
  scroll-behavior: smooth;

  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${colors.border.subtle};
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: ${colors.text.tertiary};
  }
`;

const StyledList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.md}px;
  padding: ${spacing.md}px;
`;

const StyledEmptyWrap = styled.div`
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${spacing.lg}px;
`;
